"use client";

import { useState, type ReactNode } from "react";
import { RotateCcw, Search, SlidersHorizontal } from "lucide-react";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { Checkbox } from "@/components/ui/checkbox";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { Input } from "@/components/ui/Input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetTrigger,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

const CLEAR_VALUE = "__clear__";

export interface FilterOption {
  label: string;
  value: string;
  count?: number;
}

type FilterFieldCommon = {
  id: string;
  label?: string;
  hint?: string;
  disabled?: boolean;
};

export type FilterField =
  | (FilterFieldCommon & {
      type: "search";
      value: string;
      onChange: (value: string) => void;
      placeholder?: string;
    })
  | (FilterFieldCommon & {
      type: "option-list";
      value: string;
      onChange: (value: string) => void;
      options: FilterOption[];
      allowClear?: boolean;
    })
  | (FilterFieldCommon & {
      type: "select";
      value: string;
      onChange: (value: string) => void;
      options: FilterOption[];
      placeholder?: string;
      clearLabel?: string;
    })
  | (FilterFieldCommon & {
      type: "checkbox-group";
      values: string[];
      onChange: (values: string[]) => void;
      options: FilterOption[];
    })
  | (FilterFieldCommon & {
      type: "radio-group";
      value: string;
      onChange: (value: string) => void;
      options: FilterOption[];
    })
  | (FilterFieldCommon & {
      type: "combobox";
      value: string;
      onChange: (value: string) => void;
      options: ComboboxOption[];
      placeholder?: string;
      searchPlaceholder?: string;
      /** Provide for server-driven autocomplete (debounce upstream); omit for plain
       * client-side filtering over a fixed `options` list. */
      onSearchChange?: (query: string) => void;
      isLoading?: boolean;
    });

function isFieldActive(field: FilterField): boolean {
  if (field.type === "checkbox-group") return field.values.length > 0;
  return !!field.value;
}

function FilterFieldControl({ field }: { field: FilterField }) {
  switch (field.type) {
    case "search":
      return (
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={field.value}
            disabled={field.disabled}
            onChange={(event) => field.onChange(event.target.value)}
            placeholder={field.placeholder}
            className="pl-9"
          />
        </div>
      );

    case "option-list":
      return (
        <div className="space-y-2">
          {field.options.map((option) => {
            const isActive = field.value === option.value;
            return (
              <button
                key={option.value}
                type="button"
                disabled={field.disabled}
                onClick={() =>
                  field.onChange(isActive && field.allowClear ? "" : option.value)
                }
                className={cn(
                  "flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left text-sm transition disabled:cursor-not-allowed disabled:opacity-50 dark:border-0",
                  isActive
                    ? "border-primary/30 bg-primary/10 text-primary"
                    : "border-border bg-card text-foreground/80 hover:border-primary/30 hover:bg-primary/5",
                )}
              >
                <span>{option.label}</span>
                {option.count !== undefined ? (
                  <Badge variant="secondary" className="rounded-full">
                    {option.count}
                  </Badge>
                ) : null}
              </button>
            );
          })}
        </div>
      );

    case "select":
      return (
        <Select
          value={field.value || CLEAR_VALUE}
          disabled={field.disabled}
          onValueChange={(next) =>
            field.onChange(next === CLEAR_VALUE ? "" : next)
          }
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder={field.placeholder} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={CLEAR_VALUE}>
              {field.clearLabel ?? "All"}
            </SelectItem>
            {field.options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
                {option.count !== undefined ? ` (${option.count})` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );

    case "checkbox-group":
      return (
        <div className="space-y-2.5">
          {field.options.map((option) => {
            const checked = field.values.includes(option.value);
            return (
              <label
                key={option.value}
                className="flex cursor-pointer items-center gap-2.5 text-sm"
              >
                <Checkbox
                  checked={checked}
                  disabled={field.disabled}
                  onCheckedChange={(next) => {
                    const nextValues = next
                      ? [...field.values, option.value]
                      : field.values.filter((v) => v !== option.value);
                    field.onChange(nextValues);
                  }}
                />
                <span className="flex-1 text-foreground/80">{option.label}</span>
                {option.count !== undefined ? (
                  <span className="text-xs text-muted-foreground">
                    {option.count}
                  </span>
                ) : null}
              </label>
            );
          })}
        </div>
      );

    case "radio-group":
      return (
        <RadioGroup
          value={field.value}
          onValueChange={field.onChange}
          className="gap-2.5"
        >
          {field.options.map((option) => (
            <label
              key={option.value}
              className="flex cursor-pointer items-center gap-2.5 text-sm"
            >
              <RadioGroupItem value={option.value} disabled={field.disabled} />
              <span className="flex-1 text-foreground/80">{option.label}</span>
              {option.count !== undefined ? (
                <span className="text-xs text-muted-foreground">
                  {option.count}
                </span>
              ) : null}
            </label>
          ))}
        </RadioGroup>
      );

    case "combobox":
      return (
        <Combobox
          options={field.options}
          value={field.value}
          onValueChange={field.onChange}
          placeholder={field.placeholder}
          searchPlaceholder={field.searchPlaceholder}
          onSearchChange={field.onSearchChange}
          isLoading={field.isLoading}
          disabled={field.disabled}
        />
      );

    default:
      return null;
  }
}

function FilterFieldGroup({ field }: { field: FilterField }) {
  return (
    <div className="space-y-2">
      {field.label ? (
        <label className="block text-sm font-medium text-foreground">
          {field.label}
        </label>
      ) : null}
      {field.hint ? (
        <p className="text-xs text-muted-foreground">{field.hint}</p>
      ) : null}
      <FilterFieldControl field={field} />
    </div>
  );
}

interface FilterPanelBodyProps {
  fields: FilterField[];
  footer?: ReactNode;
}

function FilterPanelBody({ fields, footer }: FilterPanelBodyProps) {
  return (
    <div className="space-y-5">
      {fields.map((field) => (
        <FilterFieldGroup key={field.id} field={field} />
      ))}
      {footer}
    </div>
  );
}

export interface FilterPanelProps {
  title?: string;
  description?: string;
  fields: FilterField[];
  /** Explicit count of active filters shown as a badge; auto-computed from `fields` if omitted. */
  activeCount?: number;
  onReset?: () => void;
  /** Extra content rendered below the fields, inside both the desktop card and mobile sheet. */
  footer?: ReactNode;
  className?: string;
}

/**
 * Responsive, config-driven filter block: a persistent card on desktop (lg+), a
 * "Filters" button that opens a slide-over Sheet on mobile. Field rendering is driven
 * entirely by the `fields` config, supporting search, option lists, selects, checkbox
 * groups, radio groups, and sync/async comboboxes.
 */
export function FilterPanel({
  title = "Filters",
  description,
  fields,
  activeCount,
  onReset,
  footer,
  className,
}: FilterPanelProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const computedActiveCount =
    activeCount ?? fields.filter(isFieldActive).length;

  return (
    <>
      <Card className={cn("hidden border-border bg-card shadow-sm lg:block", className)}>
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <SlidersHorizontal className="h-4 w-4" />
              </div>
              <div>
                <CardTitle className="text-base">{title}</CardTitle>
                {description ? (
                  <p className="text-sm text-muted-foreground">{description}</p>
                ) : null}
              </div>
            </div>
            {onReset && computedActiveCount > 0 ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 gap-1.5 px-2 text-muted-foreground"
                onClick={onReset}
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Reset
              </Button>
            ) : null}
          </div>
        </CardHeader>
        <CardContent>
          <FilterPanelBody fields={fields} footer={footer} />
        </CardContent>
      </Card>

      <div className="lg:hidden">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" className="w-full justify-between">
              <span className="flex items-center gap-2">
                <SlidersHorizontal className="h-4 w-4" />
                {title}
              </span>
              {computedActiveCount > 0 ? (
                <Badge variant="secondary" className="rounded-full">
                  {computedActiveCount}
                </Badge>
              ) : null}
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-full overflow-y-auto sm:max-w-sm">
            <SheetHeader>
              <SheetTitle>{title}</SheetTitle>
              {description ? (
                <SheetDescription>{description}</SheetDescription>
              ) : null}
            </SheetHeader>
            <div className="px-4 pb-4">
              <FilterPanelBody fields={fields} footer={footer} />
            </div>
            {onReset && computedActiveCount > 0 ? (
              <div className="border-t border-border p-4">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full gap-2"
                  onClick={() => {
                    onReset();
                  }}
                >
                  <RotateCcw className="h-4 w-4" />
                  Reset filters
                </Button>
              </div>
            ) : null}
          </SheetContent>
        </Sheet>
      </div>
    </>
  );
}
